import { useState } from 'react';
import { ArrowLeft, Check, ClipboardList, Download, Edit, FileText, Plus, Printer, Save, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import InvoiceTemplate from '../components/InvoiceTemplate';
import { showAppConfirm } from '../components/shared/AppConfirm';
import { formatCurrency, formatDate } from '../lib/format';

const PurchaseOrderModule = ({ purchaseOrders, products, suppliers, settings, onAdd, onUpdate, onReceive, onUpdateStatus, onDelete, loading, readOnly }) => {
  const [view, setView] = useState('list'); // list, create, detail
  const [selectedPO, setSelectedPO] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    supplier: '',
    supplierId: '',
    supplierName: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    items: [],
    total: 0,
    status: 'draft'
  });

  const statusColors = {
    draft: 'bg-slate-100 text-slate-600',
    ordered: 'bg-blue-100 text-blue-600',
    received: 'bg-emerald-100 text-emerald-600',
    cancelled: 'bg-rose-100 text-rose-600',
  };
  const statusLabels = { draft: 'Draft', ordered: 'Dipesan', received: 'Diterima', cancelled: 'Dibatalkan' };

  const filtered = purchaseOrders.filter(po => {
    if (statusFilter !== 'all' && po.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return po.supplier?.toLowerCase().includes(q) || po.supplierName?.toLowerCase().includes(q) || po.id?.toLowerCase().includes(q);
    }
    return true;
  });

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { productId: '', name: '', unit: 'Pcs', qty: 1, price: 0, total: 0 }] });
  };

  const removeItem = (idx) => {
    const items = [...formData.items];
    items.splice(idx, 1);
    setFormData({ ...formData, items });
  };

  const updateItem = (idx, field, value) => {
    const items = [...formData.items];
    if (field === 'productId') {
      const product = products.find(p => p.id === value);
      if (product) {
        items[idx] = { ...items[idx], productId: value, name: product.name, unit: product.unit, price: product.price };
        if (!items[idx].qty || items[idx].qty < 1) items[idx].qty = 1;
        items[idx].total = items[idx].price * items[idx].qty;
      }
    } else {
      items[idx][field] = field === 'qty' || field === 'price' ? parseInt(value) || 0 : value;
      items[idx].total = items[idx].price * items[idx].qty;
    }
    setFormData({ ...formData, items });
  };

  const calcTotal = () => formData.items.reduce((s, i) => s + i.total, 0);

  const handleSubmit = async () => {
    if ((!formData.supplierId && !formData.supplier) || formData.items.length === 0) {
      alert('Pilih supplier dan minimal 1 item');
      return;
    }
    if (formData.items.some(i => !i.productId || i.qty < 1)) {
      alert('Pilih produk dan qty minimal 1 untuk setiap item');
      return;
    }
    const data = { ...formData, total: calcTotal() };
    if (editingId) {
      await onUpdate(editingId, data);
    } else {
      await onAdd(data);
    }
    resetForm();
    setView('list');
  };

  const resetForm = () => {
    setFormData({ supplier: '', supplierId: '', supplierName: '', date: new Date().toISOString().split('T')[0], notes: '', items: [], total: 0, status: 'draft' });
    setEditingId(null);
  };

  const handleEdit = (po) => {
    if (readOnly) return;
    if (po.status === 'received') return;
    setFormData({ supplier: po.supplier, supplierId: po.supplierId || '', supplierName: po.supplierName || po.supplier, date: po.date, notes: po.notes || '', items: po.items || [], total: po.total, status: po.status });
    setEditingId(po.id);
    setView('create');
  };

  // Detail View
  if (view === 'detail' && selectedPO) {
    // Find supplier details and build an enriched record that the InvoiceTemplate
    // can render as if it were any other document (invoice/quote/sj).
    const supplierData = suppliers.find(s => s.id === selectedPO.supplierId) || {};
    // Multi-line address: alamat (bisa multi-baris) di baris awal, lalu
    // "city, state zip" di baris terpisah — ditampilkan dengan whiteSpace: 'pre-line'.
    const supplierCityStateZip = [
      [supplierData.city, supplierData.state].filter(Boolean).join(', '),
      supplierData.zip || '',
    ].filter(Boolean).join(' ').trim();
    const supplierAddress = [supplierData.address || '', supplierCityStateZip]
      .filter(Boolean)
      .join('\n');
    const previewTx = {
      ...selectedPO,
      type: 'po',
      customerName: selectedPO.supplierName || selectedPO.supplier || supplierData.name || '',
      customerEmail: supplierData.email || '',
      customerAddress: supplierAddress || (supplierData.contactPerson ? `CP: ${supplierData.contactPerson}` : ''),
    };

    const handleDownloadPdf = async () => {
      const node = document.getElementById('invoice-template');
      if (!node) {
        alert('Template tidak ditemukan.');
        return;
      }
      try {
        const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#FFFFFF', logging: false });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const tolerance = 0.5;
        if (imgHeight <= pageHeight + tolerance) {
          pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pageHeight));
        } else if (imgHeight <= pageHeight * 1.15) {
          const scaledWidth = (imgWidth * pageHeight) / imgHeight;
          const xOffset = (pageWidth - scaledWidth) / 2;
          pdf.addImage(imgData, 'JPEG', xOffset, 0, scaledWidth, pageHeight);
        } else {
          let heightLeft = imgHeight;
          let position = 0;
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          while (heightLeft > tolerance) {
            position -= pageHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
        }
        pdf.save(`PurchaseOrder_${selectedPO.id}.pdf`);
      } catch (err) {
        alert('Gagal membuat PDF: ' + (err?.message || 'unknown error'));
      }
    };

    return (
      <div className="space-y-6 animate-in">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => { setView('list'); setSelectedPO(null); }} className="btn-ghost" data-testid="po-back-btn">
              <ArrowLeft size={18} /> Kembali
            </button>
            <div className="hidden sm:block w-px h-6 bg-border" />
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-heading font-bold tracking-tight">Purchase Order</h2>
              <span className="text-sm text-muted-foreground font-mono">#{selectedPO.id}</span>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${statusColors[selectedPO.status] || statusColors.draft}`}>
                {statusLabels[selectedPO.status] || selectedPO.status}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!readOnly && selectedPO.status === 'draft' && (
              <button onClick={() => { onUpdateStatus(selectedPO.id, 'ordered'); setSelectedPO({...selectedPO, status: 'ordered'}); }} className="btn-secondary bg-accent text-accent-foreground hover:opacity-90" data-testid="po-mark-ordered">
                Tandai Dipesan
              </button>
            )}
            {!readOnly && (selectedPO.status === 'draft' || selectedPO.status === 'ordered') && (
              <button onClick={async () => { if (await showAppConfirm('Terima barang dan update stok produk?')) { onReceive(selectedPO.id); setSelectedPO({...selectedPO, status: 'received'}); } }} className="btn-secondary bg-emerald-500 text-white hover:bg-emerald-600" data-testid="po-receive-btn">
                <Check size={16}/> Terima Barang
              </button>
            )}
            <button onClick={handleDownloadPdf} className="btn-secondary" data-testid="po-download-pdf-btn">
              <Download size={16}/> Download PDF
            </button>
            <button onClick={() => window.print()} className="btn-primary" data-testid="po-print-btn">
              <Printer size={16}/> Cetak
            </button>
          </div>
        </div>

        <div id="printable-area" className="mx-auto" style={{ width: '794px', maxWidth: '100%' }}>
          <div className="card shadow-lg overflow-hidden">
            <InvoiceTemplate transaction={previewTx} settings={settings} products={products} />
          </div>
        </div>
      </div>
    );
  }

  // Create/Edit Form
  if (view === 'create') {
    return (
      <div className="space-y-6 animate-in">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold">{editingId ? 'Edit Purchase Order' : 'Purchase Order Baru'}</h2>
            <p className="text-muted-foreground mt-1">Buat pesanan pembelian untuk menambah stok</p>
          </div>
          <button onClick={() => { setView('list'); resetForm(); }} className="px-4 py-2 border border-border rounded-xl text-sm font-medium hover:bg-slate-50">
            Kembali
          </button>
        </div>

        <div className="card p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Supplier<span className="text-destructive">*</span></label>
              <select 
                className="input-field bg-white" 
                value={formData.supplierId} 
                onChange={e => {
                  const sup = suppliers.find(s => s.id === e.target.value);
                  setFormData({...formData, supplierId: e.target.value, supplier: sup?.name || '', supplierName: sup?.name || ''});
                }}
                data-testid="po-supplier-input"
              >
                <option value="">Pilih Supplier</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}{s.city ? ` - ${s.city}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Tanggal<span className="text-destructive">*</span></label>
              <input type="date" className="input-field" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} data-testid="po-date-input" />
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <h3 className="font-semibold">Item Pembelian</h3>
            <button onClick={addItem} className="btn-primary flex items-center gap-1.5 text-sm" data-testid="po-add-item">
              <Plus size={14}/> Tambah Item
            </button>
          </div>

          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Produk<span className="text-destructive">*</span></th>
                <th className="px-4 py-3 text-left">Qty<span className="text-destructive">*</span></th>
                <th className="px-4 py-3 text-left">Harga</th>
                <th className="px-4 py-3 text-right">Jumlah</th>
                <th className="px-4 py-3 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">Belum ada item. Klik &ldquo;Tambah Item&rdquo;.</td></tr>
              ) : formData.items.map((item, idx) => (
                <tr key={idx} className="table-row">
                  <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <select className="input-field" value={item.productId} onChange={e => updateItem(idx, 'productId', e.target.value)} data-testid={`po-item-product-${idx}`}>
                      <option value="">Pilih Produk</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name} (Stok: {p.stock})</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input type="number" className="input-field w-24" min="1" value={item.qty || ''} onChange={e => updateItem(idx, 'qty', e.target.value)} data-testid={`po-item-qty-${idx}`} />
                  </td>
                  <td className="px-4 py-3">
                    <input type="number" className="input-field w-32" value={item.price || ''} onChange={e => updateItem(idx, 'price', e.target.value)} data-testid={`po-item-price-${idx}`} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(item.total)}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => removeItem(idx)} className="p-1.5 hover:bg-rose-50 rounded-lg text-muted-foreground hover:text-destructive">
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end pt-2">
            <div className="text-right">
              <span className="text-muted-foreground mr-4">Total:</span>
              <span className="text-xl font-bold font-mono">{formatCurrency(calcTotal())}</span>
            </div>
          </div>

          <div>
            <label className="label">Catatan</label>
            <textarea className="input-field" rows={2} placeholder="Catatan tambahan..." value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button onClick={() => { setView('list'); resetForm(); }} className="px-6 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-slate-50">Batal</button>
            <button onClick={handleSubmit} className="btn-primary px-6 py-2.5" data-testid="po-save-btn">
              <Save size={16} className="inline mr-1.5" /> {editingId ? 'Perbarui' : 'Simpan'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Purchase Order</h2>
          <p className="text-muted-foreground mt-1">Kelola pesanan pembelian untuk menambah stok</p>
        </div>
        {!readOnly && (
        <button onClick={() => { resetForm(); setView('create'); }} className="btn-primary flex items-center gap-2" data-testid="po-create-btn">
          <Plus size={16}/> PO Baru
        </button>
        )}
      </div>

      <div className="card p-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="relative w-full lg:w-80">
            <input type="text" placeholder="Cari PO..." className="input-field pl-10" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="po-search" />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <select className="input-field w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="po-status-filter">
            <option value="all">Semua Status</option>
            <option value="draft">Draft</option>
            <option value="ordered">Dipesan</option>
            <option value="received">Diterima</option>
            <option value="cancelled">Dibatalkan</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="table-header">
            <tr>
              <th className="px-6 py-4 text-left">No</th>
              <th className="px-6 py-4 text-left">ID</th>
              <th className="px-6 py-4 text-left">Supplier</th>
              <th className="px-6 py-4 text-left">Tanggal</th>
              <th className="px-6 py-4 text-right">Total</th>
              <th className="px-6 py-4 text-center">Status</th>
              <th className="px-6 py-4 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-6 py-12 text-center text-muted-foreground">Memuat data...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="7" className="px-6 py-12 text-center">
                <div className="flex flex-col items-center gap-2">
                  <ClipboardList className="text-muted-foreground/40" size={40}/>
                  <p className="font-medium text-muted-foreground">Belum ada Purchase Order</p>
                  <p className="text-sm text-muted-foreground">Klik &ldquo;PO Baru&rdquo; untuk membuat pesanan</p>
                </div>
              </td></tr>
            ) : filtered.map((po, idx) => (
              <tr key={po.id} className="table-row cursor-pointer" onClick={() => { setSelectedPO(po); setView('detail'); }}>
                <td className="px-6 py-4 text-muted-foreground">{idx + 1}</td>
                <td className="px-6 py-4 font-mono font-semibold text-primary">#{po.id}</td>
                <td className="px-6 py-4">{po.supplierName || po.supplier}</td>
                <td className="px-6 py-4 text-muted-foreground">{formatDate(po.date)}</td>
                <td className="px-6 py-4 text-right font-mono font-semibold">{formatCurrency(po.total)}</td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[po.status]}`}>
                    {statusLabels[po.status]}
                  </span>
                </td>
                <td className="px-6 py-4 text-center" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => { setSelectedPO(po); setView('detail'); }} className="p-2 hover:bg-slate-100 rounded-lg text-muted-foreground" title="Lihat Detail" data-testid={`po-view-${po.id}`}>
                      <FileText size={16}/>
                    </button>
                    {!readOnly && po.status !== 'received' && po.status !== 'cancelled' && (
                      <button onClick={() => handleEdit(po)} className="p-2 hover:bg-blue-50 rounded-lg text-muted-foreground hover:text-blue-600" title="Edit" data-testid={`po-edit-${po.id}`}>
                        <Edit size={16}/>
                      </button>
                    )}
                    {!readOnly && (po.status === 'draft' || po.status === 'ordered') && (
                      <button onClick={async () => { if (await showAppConfirm('Terima barang dan update stok?')) onReceive(po.id); }} className="p-2 hover:bg-emerald-50 rounded-lg text-muted-foreground hover:text-emerald-600" title="Terima Barang" data-testid={`po-receive-${po.id}`}>
                        <Check size={16}/>
                      </button>
                    )}
                    {!readOnly && (
                      <button
                        onClick={async () => {
                          const isReceived = po.status === 'received';
                          const msg = isReceived
                            ? `Hapus PO #${po.id}?\n\nPO ini sudah diterima — stok produk akan DIKURANGI kembali sesuai jumlah yang tercatat.`
                            : `Hapus PO #${po.id}?`;
                          if (await showAppConfirm(msg)) {
                            onDelete(po.id);
                          }
                        }}
                        className="p-2 hover:bg-rose-50 rounded-lg text-muted-foreground hover:text-destructive"
                        title={po.status === 'received' ? 'Hapus (stok akan dikurangi)' : 'Hapus'}
                        data-testid={`po-delete-${po.id}`}
                      >
                        <Trash2 size={16}/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== INVENTORY MODULE ==============


export default PurchaseOrderModule;
